import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import { Box, DialogActions, DialogContent, Toolbar, useTheme } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { FreeTextField } from "../../FreeTextField";
import { ContainedButton } from "../../ContainedButton";
import { strings } from "../../../localization/LocalizedStrings";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../hooks/isMobile";
import { AlertDialog } from "../../AlertDialog";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";

export const ModifyFreeTextAttributeDialog: FunctionComponent<
  IModifyFreeTextAttributeDialogProps
> = ({
  title,
  description,
  placeholder,
  open,
  onClose,
  value,
  onChange,
  onClearValue,
  onCancel
}) => {
  const isMobile = useIsMobile();
  const theme = useTheme();
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onCancelledByUser();
    }
  };
  const onCancelledByUser = () => {
    formik.resetForm();
    onCancel?.();
    onClose();
  };
  const formik = useFormik({
    initialValues: {
      value: value ?? ""
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ value }) => {
      onChange(value);
      onClose();
    }
  });
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"sm"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{title}</h3>
          <Box flexGrow={1} />
          {onClearValue && (
            <ToolbarButton
              onClick={() => {
                formik.resetForm();
                onClearValue?.();
                onClose();
              }}
              tooltip={strings.clear_value}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton onClick={tryToClose} tooltip={strings.close} iconType={IconType.Close} />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <p className={styles.description}>{description}</p>
          <div>
            <FreeTextField
              id="value"
              name="value"
              className={styles.textField}
              autoFocus={!isMobile}
              placeholder={placeholder}
              value={formik.values.value}
              onChange={formik.handleChange}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onCancelledByUser}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
    </FormikProvider>
  );
};

export interface IModifyFreeTextAttributeDialogProps {
  title: string;
  description: string;
  placeholder: string;
  open: boolean;
  onClose: () => void;
  value?: string;
  onChange: (newValue: string) => void;
  onClearValue?: () => void;
  onCancel?: () => void;
}
